import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import * as businessesApi from '../endpoints/businesses';
import { ListBusinessesParams, SearchBusinessesParams, NearbyBusinessesParams, NearbyBusinessResponse, TrendingBusinessesParams, TrendingBusinessResponse, PaginatedBusinessPhotosResponse } from '../endpoints/businesses';
import { Business } from '@/types/business';
import { Paginated } from '@/types/pagination';

/**
 * Query keys for businesses
 */
export const BUSINESSES_QUERY_KEYS = {
  all: ['businesses'] as const,
  lists: () => [...BUSINESSES_QUERY_KEYS.all, 'list'] as const,
  list: (params: ListBusinessesParams) => [...BUSINESSES_QUERY_KEYS.lists(), params] as const,
  searches: () => [...BUSINESSES_QUERY_KEYS.all, 'search'] as const,
  search: (params: SearchBusinessesParams) => [...BUSINESSES_QUERY_KEYS.searches(), params] as const,
  nearby: () => [...BUSINESSES_QUERY_KEYS.all, 'nearby'] as const,
  nearbyQuery: (params: NearbyBusinessesParams) => [...BUSINESSES_QUERY_KEYS.nearby(), params] as const,
  details: () => [...BUSINESSES_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...BUSINESSES_QUERY_KEYS.details(), id] as const,
  cuisines: () => [...BUSINESSES_QUERY_KEYS.all, 'cuisines'] as const,
  trending: () => [...BUSINESSES_QUERY_KEYS.all, 'trending'] as const,
  trendingQuery: (params: TrendingBusinessesParams) => [...BUSINESSES_QUERY_KEYS.trending(), params] as const,
  photos: (id: string) => [...BUSINESSES_QUERY_KEYS.all, 'photos', id] as const,
} as const;

/**
 * Domain-oriented query options for business operations
 * Provides reusable queryOptions that can be used in hooks or prefetching
 */

/**
 * Query options for listing businesses with pagination and filtering
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Query options for the businesses list
 *
 * @example
 * ```typescript
 * // Default: Bloomington, IN, page 1
 * const options = businessesListQueryOptions();
 *
 * // Custom city/state
 * const options = businessesListQueryOptions({ city: 'San Francisco', state: 'CA' });
 *
 * // With pagination
 * const options = businessesListQueryOptions({ page: 2, per_page: 10 });
 * ```
 */
export const businessesListQueryOptions = (params: ListBusinessesParams = {}) =>
  queryOptions({
    queryKey: BUSINESSES_QUERY_KEYS.list(params),
    queryFn: async (): Promise<Paginated<Business>> => {
      return businessesApi.listBusinesses(params);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for searching businesses with text query
 *
 * @param params - Search parameters including query text, location filters, and pagination
 * @returns Query options for the businesses search
 *
 * @example
 * ```typescript
 * // Search for "pizza" in Bloomington, IN
 * const options = businessesSearchQueryOptions({
 *   query: 'pizza',
 *   city: 'Bloomington',
 *   state: 'IN',
 *   per_page: 5
 * });
 * ```
 */
export const businessesSearchQueryOptions = (params: SearchBusinessesParams = {}) =>
  queryOptions({
    queryKey: BUSINESSES_QUERY_KEYS.search(params),
    queryFn: async (): Promise<Paginated<Business>> => {
      return businessesApi.searchBusinesses(params);
    },
    enabled: (params.query?.trim().length ?? 0) >= 2, // Only search if query is at least 2 characters
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for fetching nearby businesses by location
 *
 * @param params - Nearby search parameters including latitude, longitude, radius, and limit
 * @returns Query options for nearby businesses
 *
 * @example
 * ```typescript
 * const options = businessesNearbyQueryOptions({
 *   latitude: 39.1653,
 *   longitude: -86.5264,
 *   radius_km: 5,
 *   limit: 20
 * });
 * ```
 */
export const businessesNearbyQueryOptions = (params: NearbyBusinessesParams) =>
  queryOptions({
    queryKey: BUSINESSES_QUERY_KEYS.nearbyQuery(params),
    queryFn: async (): Promise<NearbyBusinessResponse> => {
      return businessesApi.getNearbyBusinesses(params);
    },
    enabled: !!params.latitude && !!params.longitude,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      // Persist so Discover paints from disk on cold boot instead of a skeleton.
      // The persister purges on logout, so no data outlives its session.
      persist: true,
    },
  });

/**
 * Query options for fetching trending businesses by check-in count
 */
export const businessesTrendingQueryOptions = (params: TrendingBusinessesParams = {}) =>
  queryOptions({
    queryKey: BUSINESSES_QUERY_KEYS.trendingQuery(params),
    queryFn: async (): Promise<TrendingBusinessResponse> => {
      return businessesApi.getTrendingBusinesses(params);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      // Persist so Discover paints from disk on cold boot instead of a skeleton.
      persist: true,
    },
  });

/**
 * Query options for fetching a single business by ID
 *
 * @param id - Business ID
 * @returns Query options for the business detail
 *
 * @example
 * ```typescript
 * const options = businessDetailQueryOptions('business-123');
 * ```
 */
export const businessDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: BUSINESSES_QUERY_KEYS.detail(id),
    queryFn: async (): Promise<Business> => {
      return businessesApi.getBusinessById(id);
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for fetching unique cuisine types
 *
 * @returns Query options for unique cuisine types
 *
 * @example
 * ```typescript
 * const options = cuisinesQueryOptions();
 * ```
 */
export const cuisinesQueryOptions = () =>
  queryOptions({
    queryKey: BUSINESSES_QUERY_KEYS.cuisines(),
    queryFn: async (): Promise<string[]> => {
      return businessesApi.getUniqueCuisineTypes();
    },
    staleTime: 1000 * 60 * 15, // 15 minutes (cuisines don't change often)
    gcTime: 1000 * 60 * 30, // 30 minutes
    meta: {
      // Near-static list; persist so the Categories rail paints instantly.
      persist: true,
    },
  });

/**
 * Query options for fetching business photos (page 1, 10 items for strip preview)
 *
 * @param id - Business ID
 * @returns Query options for business photos
 */
export const businessPhotosQueryOptions = (id: string) =>
  queryOptions({
    queryKey: BUSINESSES_QUERY_KEYS.photos(id),
    queryFn: async (): Promise<PaginatedBusinessPhotosResponse> => {
      return businessesApi.getBusinessPhotos(id, { page: 1, per_page: 10 });
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Infinite query options for paginated business search results
 *
 * @param params - Search parameters (page is managed internally)
 * @returns Infinite query options for business search
 */
export const businessesSearchInfiniteQueryOptions = (params: Omit<SearchBusinessesParams, 'page'>) =>
  infiniteQueryOptions({
    queryKey: [...BUSINESSES_QUERY_KEYS.searches(), params, 'infinite'] as const,
    queryFn: async ({ pageParam }): Promise<Paginated<Business>> => {
      return businessesApi.searchBusinesses({ ...params, page: pageParam as number });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled: (params.query?.trim().length ?? 0) >= 2,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 15,
    meta: { persist: false },
  });

/**
 * Infinite query options for paginated business photo grid
 *
 * @param id - Business ID
 * @returns Infinite query options for business photos
 */
export const businessPhotosInfiniteQueryOptions = (id: string) =>
  infiniteQueryOptions({
    queryKey: [...BUSINESSES_QUERY_KEYS.photos(id), 'infinite'],
    queryFn: async ({ pageParam }): Promise<PaginatedBusinessPhotosResponse> => {
      return businessesApi.getBusinessPhotos(id, { page: pageParam, per_page: 30 });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 15,
  });
